import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import AddTask from '@mui/icons-material/AddTask';
import Delete from '@mui/icons-material/Delete';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { ensureArray } from '../../../../utils/validateResponseApi.js';
import { TriggerForm } from './TriggerForm';
import { FORM_FIELD_TYPE_OPTIONS } from '../../constants/interface.constants';

export const FormType = ({ values = [], onFieldsChange, t }) => {
    const initState = () => ({
        id: '',
        label: '',
        type: '',
        required: false,
        placeholder: '',
        defaultValue: '',
        values: [],
        rows: 3,
        accept: '',
        height: 250,
        subtype: 'plaintext',
        trigger: {},
        dependsOn: '',
        configuration: '',
        multiple: true
    });
    
    const [formValues, setFormValues] = useState(initState());
    const [obj, setObj] = useState(values);
    const [visibleTable, setVisibleTable] = useState(false);

    const handleAdd = () => {
        if (formValues.id && formValues.label && formValues.type) {
            const newField = { ...formValues };
            if (formValues.type === 'action_selection') {
                try {
                    newField.configuration = typeof formValues.configuration === 'string'
                        ? JSON.parse(formValues.configuration)
                        : formValues.configuration;
                } catch (error) {
                    return;
                }
            } else {
                delete newField.configuration;
                delete newField.multiple;
            }
            if (formValues.type !== 'select' && formValues.type !== 'multiple_select') delete newField.values;
            if (formValues.type !== 'textarea') delete newField.rows;
            if (formValues.type !== 'number') { delete newField.min; delete newField.max; }
            if (formValues.type !== 'select_trigger') { delete newField.trigger; delete newField.dependsOn; }
            setObj(prevItems => [...prevItems, newField]);
            setFormValues(initState());
        }
    };

    const handleDelete = (index) => {
        setObj(obj.filter((_, i) => i !== index));
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormValues({
            ...formValues,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleSelectTriggerData = React.useCallback((obj) => {
        setFormValues(prev => ({ ...prev, trigger: obj }));
    }, []);

    useEffect(() => {
        onFieldsChange(obj);
    }, [obj, onFieldsChange]);

    return (
        <Box>
            <Grid container spacing={2} alignItems="center">
                <Grid size={{xs:10, sm:10}}>
                    <Typography variant='h6' component='h6'>
                        {t("common.formFieldsTitle")}
                    </Typography>
                </Grid>
                <Grid size={{xs:2, sm:2}} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Tooltip title={t("common.addField")}>
                        <IconButton
                            onClick={handleAdd}
                            sx={{ color: 'blue' }}
                            disabled={!formValues.id || !formValues.label || !formValues.type}
                        >
                            <AddTask />
                        </IconButton>
                    </Tooltip>
                    {obj.length > 0 && (
                        <Tooltip title={visibleTable ? t("common.hideTable") : t("common.showTable")}>
                            <IconButton
                                onClick={() => setVisibleTable(!visibleTable)}
                                sx={{ color: 'blue' }}
                            >
                                {visibleTable ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </Tooltip>
                    )}
                </Grid>

                <Grid size={{xs:12, sm:6}}>
                    <TextField required fullWidth label="ID" name="id" size="small" variant="outlined"
                        value={formValues.id}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        helperText="Identificador único (ej: cliente_nombre)"
                    />
                </Grid>
                
                <Grid size={{xs:12, sm:6}}>
                    <TextField
                        required
                        fullWidth
                        label="Label"
                        size="small"
                        variant="outlined"
                        name="label"
                        value={formValues.label}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        helperText="Texto visible para el usuario"
                    />
                </Grid>
                
                <Grid size={{xs:12, sm:6}}>
                    <FormControl fullWidth size="small" required>
                        <InputLabel>{t('commonFields.type')}</InputLabel>
                        <Select label={t('commonFields.type')} name="type"
                            value={formValues.type}
                            onChange={handleChange}
                        >
                            {FORM_FIELD_TYPE_OPTIONS.map((option, idx) => <MenuItem key={idx} value={option.value}>{option.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                

                {formValues.type === 'upload' && (
                    <Grid size={{xs:12, sm:6}}>
                        <TextField fullWidth size="small" name="accept" label={t('commonFields.acceptedFormats')}
                            value={formValues.accept}
                            onChange={handleChange}
                            placeholder=".pdf,.jpg,.png"
                            InputLabelProps={{ shrink: true }}
                            helperText="Separados por coma. Ej: .pdf,.jpg,.png"
                        />
                    </Grid>
                )}

                {formValues.type === 'select_trigger' && (
                    <>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField fullWidth variant="outlined" size="small" name="dependsOn" label={t('commonFields.dependsOn')}
                                value={formValues.dependsOn || ''}
                                onChange={handleChange}
                                helperText={t('messages.dependsOnHelper')}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <TriggerForm values={ensureArray(formValues?.trigger)[0]} errorInputs={{}} onFieldsChange={handleSelectTriggerData} t={t} />
                        </Grid>
                    </>
                )}

                {formValues.type === 'action_selection' && (
                    <>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                required
                                fullWidth
                                multiline
                                minRows={6}
                                name="configuration"
                                label="Configuración JSON del formulario modal"
                                value={formValues.configuration}
                                onChange={handleChange}
                                placeholder='{"form":[{"id":"campo","label":"Campo","type":"text","required":true}]}'
                                helperText="Cada registro se guardará dentro de un arreglo en el ID de este campo."
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="multiple"
                                        checked={formValues.multiple}
                                        onChange={handleChange}
                                    />
                                }
                                label="Permitir varios registros"
                            />
                        </Grid>
                    </>
                )}

                {(formValues.type === 'html' || formValues.type === 'MLEditor') && (
                    <>
                        <Grid size={{xs:12, sm:6}}>
                            <TextField fullWidth type="number" size="small" name="height" label={`${t('commonFields.height')} (px)`}
                                value={formValues.height}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        {formValues.type === 'MLEditor' && (
                            <Grid size={{xs:12, sm:6}}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>{t('commonFields.subtype')}</InputLabel>
                                    <Select name="subtype" label={t('commonFields.subtype')}
                                        value={formValues.subtype}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="plaintext">Texto plano</MenuItem>
                                        <MenuItem value="html">HTML</MenuItem>
                                        <MenuItem value="markdown">Markdown</MenuItem>
                                        <MenuItem value="json">JSON</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                    </>
                )}

                <Grid size={{xs:12, sm:6}}>
                    <FormControlLabel control={<Switch name="required" checked={formValues.required || false} onChange={handleChange} />} label={t('commonFields.required')} />
                </Grid>
            </Grid>

            {obj.length > 0 && visibleTable && (
                <TableContainer sx={{ marginTop: 3 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell width={50}>{t('common.actions')}</TableCell>
                                <TableCell>ID</TableCell>
                                <TableCell>Label</TableCell>
                                <TableCell>{t('commonFields.type')}</TableCell>
                                <TableCell>{t('commonFields.required')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {obj.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <IconButton size="small" color="error" onClick={() => handleDelete(index)}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                    <TableCell>{row.id}</TableCell>
                                    <TableCell>{row.label}</TableCell>
                                    <TableCell>{row.type}</TableCell>
                                    <TableCell>{row.required ? 'Sí' : 'No'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};
